import { type ReportTemplate } from 'reportingSolution/constants'

const config: ReportTemplate = {
  id: 'channel_grouping_performance_report_v2',
  active: true,
  deprecation_date: '2027-04-01',
  deprecation_alternative_template_id: 'reporting_base',
  label: 'Channel Grouping Performance Report V2',
  category: 'General',
  description:
    'A newer version of the Channel Grouping Performance report, that allows you to have a smooth transition from UA reporting to GA4 reporting, by simply merging the two datasets together. <br> It also joins platform data such as cost, impressions & clicks unto the Google Analytics dataset.',
  image_url: 'https://lh3.google.com/u/0/d/1zPmELvMA72wdGa8n8XC_Lag2JRH4l5rQ=w2368-h1426-iv1',
  authors: [
    {
      name: 'Tobias Hink',
      email: 'hink@precisdigital.com',
      image_url: 'https://lh3.googleusercontent.com/a-/AOh14GjyNEc4WJk3HgY11avoS-2opMitZKNbLBZcaWvS=s126-p-k-rw-no'
    },
    {
      name: 'Jonas Bæk',
      email: 'jonas@precisdigital.com',
      image_url: 'https://lh3.googleusercontent.com/ogw/AOLn63HCWKXxP5vd6YL-Mw1-yI8FZQ-5i7IZ3Cp6pWV8cg=s256-c-mo'
    }
  ],
  documentation: {
    type: 'knowledge_hub',
    link: 'https://help.alvie.io/en/articles/174320-channel-grouping-performance-report-v2-overview',
    link_description: 'Knowledge Hub: Channel Grouping Performance Report V2'
  },
  dashboard: {
    template_report_id: 'cd2d5e31-b675-4322-ad4e-e70c86ae64ac',
    report_name_template: 'copy of Channel Grouping Performance Report V2 - {today}',
    datasources: [
      {
        alias: 'main',
        datasourceName: '{name} - Cross Channel Grouping Performance V2',
        type: 'TABLE',
        tableId: 'channel_grouping_performance_report_v2_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      },
      {
        alias: 'gaUnion',
        datasourceName: '{name} - Cross Channel Grouping Performance V2 - GA Union',
        type: 'TABLE',
        tableId: 'channel_grouping_performance_report_v2_ga_union_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      },
      {
        alias: 'platformUnion',
        datasourceName: '{name} - Cross Channel Grouping Performance V2 - Platform Union',
        type: 'TABLE',
        tableId: 'channel_grouping_performance_report_v2_platforms_union_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      }
    ]
  },
  sources: {
    ga4: {
      reports: ['campaign_performance']
    },
    google_analytics: {
      reports: ['campaign_performance_hostname'],
      optional: true
    },
    gads: {
      reports: ['campaign_performance_base'],
      optional: true
    },
    bing: {
      reports: ['campaign_performance'],
      optional: true
    },
    facebook: {
      reports: ['campaign_performance_base'],
      optional: true
    },
    dv360: {
      reports: ['campaign_performance_base'],
      optional: true
    }
  },
  inputs: {
    ga4: [
      {
        id: 'ga4CustomChannelGrouping',
        type: 'case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Google Analytics 4.',
        required: true,
        mutable: true,
        dimensions: [
          {
            value: 'view_or_property_id',
            label: 'Property ID',
            type: 'string'
          },
          {
            value: 'hostname',
            label: 'Hostname',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          },
          {
            value: 'source',
            label: 'Source',
            type: 'string'
          },
          {
            value: 'medium',
            label: 'Medium',
            type: 'string'
          },
          {
            value: 'source_medium',
            label: 'Source / Medium',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Direct',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: '(direct)'
                }
              ]
            }
          },
          {
            name: 'Affiliates',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'medium',
                  value: 'affiliate'
                }
              ]
            }
          },
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'prospecting'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'display'
                }
              ]
            }
          },
          {
            name: 'Organic Search',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'google'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'bing'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'yahoo'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'duckduckgo'
                }
              ],
              '1': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'organic'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Shopping',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'shopping'
                }
              ]
            }
          },
          {
            name: 'Paid Search - PMax',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'pmax'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'performance max'
                }
              ]
            }
          },
          {
            name: 'Paid Search - nonBrand',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Retargeting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpm'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'retargeting'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpm'
                }
              ]
            }
          },
          {
            name: 'Organic Social',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'facebook'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'instagram'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'pinterest'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'snapchat'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'tiktok'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'linkedin'
                }
              ]
            }
          },
          {
            name: 'Email',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'email'
                }
              ]
            }
          },
          {
            name: 'Referral',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'referral'
                }
              ]
            }
          },
          {
            name: 'Price Comparison Sites',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'idealo'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'billiger'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'kelkoo'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'bestcheck'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'pricerunner'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'prisjakt'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'ga4MarketChannelGrouping',
        type: 'case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Google Analytics 4.',
        required: true,
        mutable: true,
        dimensions: [
          {
            value: 'view_or_property_id',
            label: 'Property ID',
            type: 'string'
          },
          {
            value: 'hostname',
            label: 'Hostname',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          },
          {
            value: 'source',
            label: 'Source',
            type: 'string'
          },
          {
            value: 'medium',
            label: 'Medium',
            type: 'string'
          },
          {
            value: 'source_medium',
            label: 'Source / Medium',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-dk-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'denmark'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-se-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'sweden'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-no-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'norway'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-fi-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'finland'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-de-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'germany'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    google_analytics: [
      {
        id: 'googleAnalyticsCustomChannelGrouping',
        type: 'case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Google Analytics.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'view_or_property_id',
            label: 'View ID',
            type: 'string'
          },
          {
            value: 'hostname',
            label: 'Hostname',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          },
          {
            value: 'source',
            label: 'Source',
            type: 'string'
          },
          {
            value: 'medium',
            label: 'Medium',
            type: 'string'
          },
          {
            value: 'source_medium',
            label: 'Source / Medium',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Direct',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: '(direct)'
                }
              ]
            }
          },
          {
            name: 'Affiliates',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'medium',
                  value: 'affiliate'
                }
              ]
            }
          },
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'prospecting'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'display'
                }
              ]
            }
          },
          {
            name: 'Organic Search',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'google'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'bing'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'yahoo'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'duckduckgo'
                }
              ],
              '1': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'organic'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Shopping',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'shopping'
                }
              ]
            }
          },
          {
            name: 'Paid Search - PMax',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'pmax'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'performance max'
                }
              ]
            }
          },
          {
            name: 'Paid Search - nonBrand',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Retargeting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpm'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'retargeting'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpm'
                }
              ]
            }
          },
          {
            name: 'Organic Social',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'facebook'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'instagram'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'pinterest'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'snapchat'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'tiktok'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'linkedin'
                }
              ]
            }
          },
          {
            name: 'Email',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'email'
                }
              ]
            }
          },
          {
            name: 'Referral',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'referral'
                }
              ]
            }
          },
          {
            name: 'Price Comparison Sites',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'idealo'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'billiger'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'kelkoo'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'bestcheck'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'pricerunner'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'prisjakt'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'googleAnalyticsMarketChannelGrouping',
        type: 'case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Google Analytics.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'view_or_property_id',
            label: 'View ID',
            type: 'string'
          },
          {
            value: 'hostname',
            label: 'Hostname',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          },
          {
            value: 'source',
            label: 'Source',
            type: 'string'
          },
          {
            value: 'medium',
            label: 'Medium',
            type: 'string'
          },
          {
            value: 'source_medium',
            label: 'Source / Medium',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-dk-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'denmark'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-se-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'sweden'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-no-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'norway'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-fi-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'finland'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-de-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'germany'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    gads: [
      {
        id: 'gadsCustomChannelGrouping',
        type: 'case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Google Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'accountid',
            label: 'Account ID',
            type: 'string'
          },
          {
            value: 'accountname',
            label: 'Account Name',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'display'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'display'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Shopping',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'shopping'
                }
              ]
            }
          },
          {
            name: 'Paid Search - PMax',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'pmax'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'performance max'
                }
              ]
            }
          },
          {
            name: 'Paid Search - nonBrand',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'campaign',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'gadsMarketChannelGrouping',
        type: 'case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Google Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'accountid',
            label: 'Account ID',
            type: 'string'
          },
          {
            value: 'accountname',
            label: 'Account Name',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    bing: [
      {
        id: 'bingCustomChannelGrouping',
        type: 'case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Microsoft Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'accountid',
            label: 'Account ID',
            type: 'string'
          },
          {
            value: 'accountname',
            label: 'Account Name',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'display'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'display'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Shopping',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'shopping'
                }
              ]
            }
          },
          {
            name: 'Paid Search - nonBrand',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'campaign',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'bingMarketChannelGrouping',
        type: 'case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Microsoft Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'accountid',
            label: 'Account ID',
            type: 'string'
          },
          {
            value: 'accountname',
            label: 'Account Name',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    facebook: [
      {
        id: 'facebookCustomChannelGrouping',
        type: 'case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Meta Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'accountid',
            label: 'Account ID',
            type: 'string'
          },
          {
            value: 'accountname',
            label: 'Account Name',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Paid Social - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'prospecting'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'facebookMarketChannelGrouping',
        type: 'case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Meta Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'accountid',
            label: 'Account ID',
            type: 'string'
          },
          {
            value: 'accountname',
            label: 'Account Name',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    dv360: [
      {
        id: 'dv360CustomChannelGrouping',
        type: 'case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in DV360.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'accountid',
            label: 'Account ID',
            type: 'string'
          },
          {
            value: 'accountname',
            label: 'Account Name',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'prospecting'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'dv360MarketChannelGrouping',
        type: 'case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in DV360.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'accountid',
            label: 'Account ID',
            type: 'string'
          },
          {
            value: 'accountname',
            label: 'Account Name',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'accountname',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    other: [
      {
        id: 'ga4MigrationDate',
        type: 'date',
        label: 'GA4 Migration Date',
        helperText:
          'The date where you switched to GA4. Used to determine when to change reporting numbers from UA to GA4.',
        required: true,
        defaultValue: '2023-06-30',
        mutable: true
      },
      {
        id: 'targetCurrency',
        type: 'dropdown',
        label: 'Target Currency',
        helperText: 'The currency to transform all currency-based columns (cost, revenue, etc) to.',
        required: true,
        defaultValue: 'EUR',
        mutable: true
      }
    ]
  }
}

export default config
